import { useCallback, useMemo } from 'react';
import { useMap } from 'react-use';

import ErrorReporter from '@lyearn/core/utils/ErrorReporter';

import useBaseEvents from './hooks/useBaseEvents';
import {
  AnyComponentType,
  AnyStateType,
  BaseEventsUnion,
  BaseEventType,
} from './hooks/useBaseEvents/events';
import useBaseValidations from './hooks/useBaseValidations';
import { EventHandlerActionsType } from './types';

const useEventHandler = <
  S extends AnyStateType = AnyStateType,
  C extends AnyComponentType = AnyComponentType,
  E extends BaseEventType = BaseEventType,
>({
  events,
}: {
  events?: {
    [key in E['type']]: (event: E, actions: EventHandlerActionsType<S, C>) => void;
  };
} = {}) => {
  const [eventHandlerUIState, eventHandlerUIActions] = useMap<S>();
  const [componentsState, componentsActions] = useMap<C>();
  const baseEvents = useBaseEvents<S, C>();

  const allEvents = useMemo<{
    [key: string]: (event: any, actions: EventHandlerActionsType<S, C>) => void;
  }>(() => {
    return { ...baseEvents, ...events };
  }, [baseEvents, events]);

  const baseValidations: {
    [key: string]: (event: any) => boolean;
  } = useBaseValidations();

  const onEventEmitter = useCallback(
    (eventData?: BaseEventsUnion<S, C> | E) => {
      if (!eventData) return;
      const event = allEvents[eventData?.type];
      if (!event) {
        ErrorReporter.error(new Error(`Event ${eventData?.type} not found`));
        return;
      }

      if (eventData.validations) {
        const isValid = eventData.validations.every((valid) => {
          const validation = baseValidations[valid.type];
          if (!validation) {
            ErrorReporter.error(new Error(`Validation ${valid.type} not found`));
            return false;
          }
          return validation(valid);
        });

        if (!isValid) {
          return;
        }
      }

      event(eventData, {
        eventHandlerUIActions,
        componentsActions,
      });
    },
    [allEvents, baseValidations, componentsActions, eventHandlerUIActions],
  );

  const onCloseComponent = useCallback(
    (componentName: string) => {
      onEventEmitter({
        type: 'hide-component',
        params: {
          Component: componentName,
        },
      });
    },
    [onEventEmitter],
  );

  return {
    onEventEmitter,
    eventHandlerUIState,
    onCloseComponent,
    componentsActions,
    componentsState,
  };
};

export default useEventHandler;
export type UseEventHandlerType = ReturnType<typeof useEventHandler>;
